import i18n from '../i18n'
import { faDigits } from './numerals'

/**
 * The alert vocabulary of design page 16 (`alerts`; P-alerts of the go-live
 * plan), mirrored from the Android app's `AlertCatalogue.kt` so both clients
 * ask the same question in the same words with the same tone.
 *
 * A dialog is a plain spec — tone, icon, copy, two labels — and
 * `components/common/Dialog.jsx` draws it. Every dialog is a function of the
 * numbers it mentions, so «حذف ۳ ضبط» is never a hardcoded three. These are
 * plain functions, not components, so they read `i18n.t()` directly rather
 * than the `useTranslation()` hook — the `alerts` namespace.
 */

const t = (key, options) => i18n.t(key, { ns: 'alerts', ...options })

export const TONES = ['brand', 'warn', 'bad', 'accent']
export const ICONS = ['warning', 'card', 'refresh', 'trash', 'flame', 'power']

/** How long a toast stays before it goes on its own. */
export const TOAST_MS = 4000

/** The tinted icon tile per tone: the tint behind, the tone's colour on the stroke. */
export const TONE_TILE = {
  brand: 'bg-brand-50 text-brand-600',
  warn: 'bg-warn-tint text-warn',
  bad: 'bg-bad-tint text-bad',
  accent: 'bg-accent-tint text-accent-ink',
}

/** Leaving a practice or review with an unsent take of `seconds`. */
export function discardTakeDialog(seconds) {
  return {
    tone: 'warn',
    icon: 'warning',
    title: t('discardTake.title'),
    body: t('discardTake.body', { seconds: faDigits(seconds) }),
    confirmLabel: t('discardTake.confirmLabel'),
    cancelLabel: t('discardTake.cancelLabel'),
  }
}

export function cancelAutopayDialog() {
  return {
    tone: 'bad',
    icon: 'warning',
    title: t('cancelAutopay.title'),
    body: t('cancelAutopay.body'),
    note: t('cancelAutopay.note'),
    confirmLabel: t('cancelAutopay.confirmLabel'),
    cancelLabel: t('cancelAutopay.cancelLabel'),
  }
}

/** The mandate confirmation: `day` of each month, a ceiling of `ceilingThousand` thousand toman. */
export function enableAutopayDialog(day, ceilingThousand) {
  return {
    tone: 'brand',
    icon: 'card',
    title: t('enableAutopay.title'),
    body: t('enableAutopay.body', { day: faDigits(day), ceilingThousand: faDigits(ceilingThousand) }),
    note: t('enableAutopay.note'),
    confirmLabel: t('enableAutopay.confirmLabel'),
    cancelLabel: t('enableAutopay.cancelLabel'),
  }
}

export function payFailedDialog() {
  return {
    tone: 'bad',
    icon: 'refresh',
    title: t('payFailed.title'),
    body: t('payFailed.body'),
    note: t('payFailed.note'),
    confirmLabel: t('payFailed.confirmLabel'),
    cancelLabel: t('payFailed.cancelLabel'),
  }
}

export function deleteRecordingsDialog(count) {
  return {
    tone: 'bad',
    icon: 'trash',
    title: t('deleteRecordings.title', { count: faDigits(count) }),
    body: t('deleteRecordings.body'),
    confirmLabel: t('deleteRecordings.confirmLabel'),
    cancelLabel: t('deleteRecordings.cancelLabel'),
  }
}

/** Spending a streak shield tonight: the streak it saves and the shields left after. */
export function shieldDialog(streakDays, shieldsAfter) {
  return {
    tone: 'accent',
    icon: 'flame',
    title: t('shield.title'),
    body: t('shield.body', { streakDays: faDigits(streakDays), shieldsAfter: faDigits(shieldsAfter) }),
    confirmLabel: t('shield.confirmLabel'),
    cancelLabel: t('shield.cancelLabel'),
  }
}

export function logoutDialog() {
  return {
    tone: 'brand',
    icon: 'power',
    title: t('logout.title'),
    body: t('logout.body'),
    confirmLabel: t('logout.confirmLabel'),
    cancelLabel: t('logout.cancelLabel'),
  }
}

/**
 * The design's one rule about the primary button: a destructive confirmation
 * is solid red, every other dialog is the brand. Tone decides — not the
 * wording — so a new dialog cannot be red by accident.
 */
export function isDestructive(spec) {
  return spec.tone === 'bad'
}

/** The two inline banners the catalogue demonstrates: they inform, they never block. */
export const BANNERS = {
  get offline() {
    return { tone: 'warn', icon: 'offline', text: t('banners.offline') }
  },
  get payFailed() {
    return { tone: 'bad', icon: 'warning', text: t('banners.payFailed') }
  },
}

/**
 * The catalogue page's six demo rows, in the design's order, each with the
 * toast its confirmation shows. The numbers are the design's sample values;
 * the live flows pass their own.
 */
export function alertDemos() {
  return [
    { key: 'discard', label: t('demos.discard.label'), note: t('demos.discard.note'), spec: () => discardTakeDialog(48), done: t('demos.discard.done') },
    { key: 'cancelAutopay', label: t('demos.cancelAutopay.label'), note: t('demos.cancelAutopay.note'), spec: cancelAutopayDialog, done: t('demos.cancelAutopay.done') },
    { key: 'payFailed', label: t('demos.payFailed.label'), note: t('demos.payFailed.note'), spec: payFailedDialog, done: t('demos.payFailed.done') },
    { key: 'deleteRecs', label: t('demos.deleteRecs.label'), note: t('demos.deleteRecs.note'), spec: () => deleteRecordingsDialog(3), done: t('demos.deleteRecs.done', { count: faDigits(3) }) },
    { key: 'shield', label: t('demos.shield.label'), note: t('demos.shield.note'), spec: () => shieldDialog(12, 1), done: t('demos.shield.done') },
    { key: 'logout', label: t('demos.logout.label'), note: t('demos.logout.note'), spec: logoutDialog, done: t('demos.logout.done') },
  ]
}

/** The sentence the catalogue's toast button shows. */
export function demoToast() {
  return t('demoToast')
}
